import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Agents as AgentsApi } from '../lib/api';
import { Pill, Spinner, EmptyState } from '../components/Common';
import type { AgentSummary } from '@freebuff/shared';
import clsx from '../lib/clsx';

export function Agents() {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState<{ id: string; name: string; key: string } | null>(null);

  const refresh = () => AgentsApi.list().then((r) => setAgents(r.agents));

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const remove = async (id: string) => {
    if (!confirm('Remover este agente e todos os dados associés?')) return;
    await AgentsApi.remove(id);
    await refresh();
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="text-lg font-semibold">Agentes monitorados</h2>
            <p className="text-xs text-slate-400">Cada VPS executa o agente FreeBuff para enviar métricas ao painel.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Novo agente</button>
        </div>
        <div className="card-body">
          {loading ? <Spinner /> :
            agents.length === 0 ? (
              <EmptyState
                title="Nenhum agente cadastrado"
                hint="Crie um agente para gerar a chave de instalação."
                action={<button onClick={() => setShowAdd(true)} className="btn btn-primary">+ Novo agente</button>}
              />
            ) : (
              <div className="overflow-x-auto -mx-5">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Host</th>
                      <th>SO</th>
                      <th>Status</th>
                      <th>Criado</th>
                      <th>Visto</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((a) => (
                      <tr key={a.id}>
                        <td className="font-semibold text-white"><Link to={`/agents/${a.id}`} className="hover:text-accent">{a.name}</Link></td>
                        <td className="font-mono text-xs text-slate-400">{a.hostname ?? '—'}</td>
                        <td className="text-slate-400 text-sm">{a.os ?? '—'}</td>
                        <td>{a.openAlerts && a.openAlerts > 0
                          ? <Pill tone="error">{a.openAlerts} alerta(s)</Pill>
                          : <Pill tone={a.online ? 'ok' : 'error'}>{a.online ? 'Online' : 'Offline'}</Pill>}</td>
                        <td className="text-slate-400 text-xs">{fmtTime(a.createdAt)}</td>
                        <td className="text-slate-400 text-xs">{fmtTime(a.lastSeenAt)}</td>
                        <td className="text-right">
                          <button onClick={() => remove(a.id)} className="btn text-danger hover:text-red-400">Remover</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      </div>

      {showAdd && (
        <Modal title="Novo agente" onClose={() => setShowAdd(false)}>
          {!lastCreated ? (
            <NewAgentForm
              busy={creating}
              onCancel={() => setShowAdd(false)}
              onSubmit={async (name, hostname, os) => {
                setCreating(true);
                try {
                  const { agent } = await AgentsApi.create(name, hostname, os);
                  setLastCreated({ id: agent.id, name: agent.name, key: agent.apiKey });
                  await refresh();
                } finally { setCreating(false); }
              }}
            />
          ) : (
            <InstallInstructions agent={lastCreated} onDone={() => { setShowAdd(false); setLastCreated(null); }} />
          )}
        </Modal>
      )}
    </div>
  );
}

function NewAgentForm({ busy, onSubmit, onCancel }: {
  busy: boolean;
  onSubmit: (name: string, hostname: string, os: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [hostname, setHostname] = useState('');
  const [os, setOs] = useState('');
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(name.trim(), hostname.trim() || `${name.trim()}.local`, os.trim()); }}
      className="space-y-4"
    >
      <div>
        <label className="label">Nome amigável do agente</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} className="input" placeholder="Ex.: Produção São Paulo" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Hostname (opcional)</label>
          <input value={hostname} onChange={(e) => setHostname(e.target.value)} maxLength={255} className="input" placeholder="vps.exemplo.com" />
        </div>
        <div>
          <label className="label">Sistema (opcional)</label>
          <input value={os} onChange={(e) => setOs(e.target.value)} maxLength={60} className="input" placeholder="Ubuntu 22.04" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn">Cancelar</button>
        <button type="submit" disabled={busy || !name} className="btn btn-primary">{busy ? 'Criando…' : 'Criar agente'}</button>
      </div>
    </form>
  );
}

function InstallInstructions({ agent, onDone }: { agent: { id: string; name: string; key: string }; onDone: () => void }) {
  const cmd = `freebuff-agent install --panel "$PANEL_URL" --id ${agent.id} --key ${agent.key}`;
  return (
    <div className="space-y-4">
      <div className={clsx('rounded-lg border border-warn/30 bg-warn/5 px-4 py-3 text-sm text-warn')}>
        ⚠️ Esta chave API aparece <b>apenas uma vez</b>. Copie agora e guarde em local seguro.
      </div>
      <div>
        <label className="label">API Key (copie e guarde)</label>
        <div className="flex gap-2">
          <input readOnly value={agent.key} className="input font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
          <button type="button" className="btn" onClick={() => navigator.clipboard?.writeText(agent.key)}>Copiar</button>
        </div>
      </div>
      <div>
        <label className="label">Comando de instalação (no VPS)</label>
        <pre className="bg-bg-subtle border border-border rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap">{cmd}</pre>
        <p className="text-xs text-slate-500 mt-2">
          Antes de rodar, defina <code className="font-mono text-slate-300">PANEL_URL</code> (env var) com a URL pública deste painel (ex.: <code>https://panel.seudominio.com</code>).
          O instalador cria <code className="font-mono">/etc/freebuff/agent.json</code> e o serviço systemd <code className="font-mono">freebuff-agent.service</code>.
        </p>
      </div>
      <div className="text-right">
        <button className="btn btn-primary" onClick={onDone}>Concluído</button>
      </div>
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center z-30 p-4 animate-fadeIn" onClick={onClose}>
      <div className={clsx('card w-full max-w-xl')} onClick={(e) => e.stopPropagation()}>
        <div className="card-header"><h3 className="font-semibold text-lg">{title}</h3><button onClick={onClose} className="text-slate-400 hover:text-white">×</button></div>
        <div className="card-body">{children}</div>
      </div>
    </div>
  );
}

function fmtTime(ts: number | null): string {
  if (!ts) return '—';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'agora';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m atrás`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h atrás`;
  return new Date(ts).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
